import axios from 'axios'
import { mockVariationAssessment, mockVariationSummaries } from './mock-data'
import { PaginatedResult, VariationAssessment, VariationAssessmentSummary } from '../models'

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/VariationAssessments`
  : undefined

export const variationAssessmentsApiBaseUrl = API_URL

const ROLE_API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/Role`
  : undefined

export type VariationUserLookupItem = {
  id: string
  displayName: string
  email: string
  roles: string[]
}

export type RoleLookupItem = {
  id: string
  name?: string
  displayId?: number
}

export type UserSignatureResponse = {
  userId: string
  fileName: string
  contentType?: string
  size?: number
}

const delay = (ms = 600) => new Promise((resolve) => setTimeout(resolve, ms))

type SortValue = string | number | null | undefined

const parseSort = (sort?: string) => {
  if (!sort) return undefined
  const parts = sort.trim().split(/\s+/)
  if (!parts.length || !parts[0]) return undefined
  const field = parts[0]
  const dir = parts[1]?.toLowerCase() === 'desc' ? 'desc' : 'asc'
  return { fieldKey: field.toLowerCase(), dir }
}

const parseDateValue = (value?: string | null) => {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const resolveMockSortValue = (item: VariationAssessmentSummary, fieldKey: string): SortValue => {
  switch (fieldKey) {
    case 'created':
    case 'createdat':
      return parseDateValue(item.updatedAt ?? item.dateSubmitted)
    case 'updatedat':
      return parseDateValue(item.updatedAt)
    case 'datesubmitted':
      return parseDateValue(item.dateSubmitted)
    case 'responsedate':
      return parseDateValue(item.responseDate)
    case 'assessmentamount':
      return item.assessmentAmount ?? null
    case 'variationagedays':
      return item.variationAgeDays ?? null
    case 'variationnumber':
    case 'variationenumber':
      return item.variationNumber
    case 'description':
      return item.description
    case 'statuslabel':
      return item.statusLabel
    case 'variationtype':
      return item.variationType ?? ''
    case 'reason':
      return item.reason ?? ''
    case 'fundingsource':
      return item.fundingSource ?? ''
    case 'contractname':
      return item.contractName
    case 'workflowstatus':
      return item.workflowStatus ?? ''
    default:
      return null
  }
}

const compareSortValues = (left: SortValue, right: SortValue) => {
  if (left === right) return 0
  if (left === null || left === undefined) return 1
  if (right === null || right === undefined) return -1
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right
  }
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

const sortMockVariationSummaries = (items: VariationAssessmentSummary[], sort?: string) => {
  const parsed = parseSort(sort)
  if (!parsed) return items
  const sorted = [...items]
  sorted.sort((left, right) => {
    const comparison = compareSortValues(
      resolveMockSortValue(left, parsed.fieldKey),
      resolveMockSortValue(right, parsed.fieldKey)
    )
    return parsed.dir === 'desc' ? -comparison : comparison
  })
  return sorted
}

type DateSearch = {
  year?: number
  month?: number
  exact?: string
}

const isValidYear = (year: number) => year >= 1900 && year <= 2100

const parseDateSearch = (term: string): DateSearch => {
  const trimmed = term.trim()
  if (!trimmed) return {}

  if (/^\d{4}$/.test(trimmed)) {
    const year = Number(trimmed)
    return isValidYear(year) ? { year } : {}
  }

  const yearMonthMatch = trimmed.match(/^(?<first>\d{1,4})[/-](?<second>\d{1,4})$/)
  if (yearMonthMatch?.groups) {
    const first = Number(yearMonthMatch.groups.first)
    const second = Number(yearMonthMatch.groups.second)
    let year = 0
    let month = 0

    if (first >= 1000) {
      year = first
      month = second
    } else if (second >= 1000) {
      year = second
      month = first
    }

    if (isValidYear(year) && month >= 1 && month <= 12) {
      return { year, month }
    }
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return {}
  }

  return { exact: parsed.toISOString().slice(0, 10) }
}

const matchDateSearch = (value: string | undefined, search: DateSearch) => {
  if (!value) return false
  if (search.exact) return value === search.exact
  if (search.year && search.month) {
    return value.startsWith(`${search.year}-${String(search.month).padStart(2, '0')}`)
  }
  if (search.year) return value.startsWith(`${search.year}-`)
  return false
}

const extractNumericSearchTerm = (term: string) => {
  const numeric = term.replace(/[^\d.]/g, '').trim()
  return numeric.length ? numeric : ''
}

const normalizeText = (value?: string | number | null) => String(value ?? '').trim().toLowerCase()

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const matchesSearchText = (value: string | number | null | undefined, search: string) => {
  const source = String(value ?? '').trim()
  const term = search.trim()
  if (!source || !term) return false
  if (!term.includes('*')) return source.toLowerCase().includes(term.toLowerCase())

  return new RegExp(escapeRegExp(term).replace(/\\\*/g, '.*'), 'i').test(source)
}

const applyMockSearch = (items: VariationAssessmentSummary[], search?: string) => {
  if (!search?.trim()) return items

  const numericTerm = extractNumericSearchTerm(search)
  const dateSearch = parseDateSearch(search)

  return items.filter((item) => {
    const textFields = [
      item.variationNumber,
      item.description,
      item.statusLabel,
      item.variationType,
      item.reason,
      item.fundingSource,
      item.contractName,
      item.workflowStatus,
      item.documentStatus,
      item.createdBy,
    ]

    if (textFields.some((field) => matchesSearchText(field, search))) {
      return true
    }

    if (matchDateSearch(item.dateSubmitted, dateSearch) || matchDateSearch(item.responseDate, dateSearch)) {
      return true
    }

    if (numericTerm) {
      if (item.assessmentAmount !== null && item.assessmentAmount !== undefined) {
        if (String(item.assessmentAmount).includes(numericTerm)) return true
      }
      if (item.variationAgeDays !== null && item.variationAgeDays !== undefined) {
        if (String(item.variationAgeDays).includes(numericTerm)) return true
      }
    }

    return false
  })
}

const normalizeDateFilterValue = (value: unknown) => {
  if (!value) return undefined
  const parsed = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(parsed.getTime())) return undefined
  return parsed.toISOString().slice(0, 10)
}

const revisionSuffixRegex = /(?:^|[-_\s]+)rev[-_\s]*(\d+)\s*$/i

const appendNextRevisionSuffix = (value?: string) => {
  const source = (value ?? '').trim()
  if (!source) {
    return 'Rev-1'
  }

  const match = source.match(revisionSuffixRegex)
  if (!match) {
    return `${source}-Rev-1`
  }

  const base = source.replace(revisionSuffixRegex, '').replace(/[-_\s]+$/, '').trim()
  const parsed = Number.parseInt(match[1], 10)
  const nextRevision = Number.isFinite(parsed) ? Math.max(parsed + 1, 1) : 1
  return base ? `${base}-Rev-${nextRevision}` : `Rev-${nextRevision}`
}

const applyMockFilters = (items: VariationAssessmentSummary[], filter?: string) => {
  if (!filter) return items

  let parsed: Record<string, unknown> | null = null
  try {
    parsed = JSON.parse(filter) as Record<string, unknown>
  } catch {
    return items
  }

  if (!parsed || Object.keys(parsed).length === 0) return items

  return items.filter((item) =>
    Object.entries(parsed as Record<string, unknown>).every(([key, rawValue]) => {
      if (rawValue === null || rawValue === undefined || rawValue === '') return true
      const value = String(rawValue).trim()
      const normalizedValue = value.toLowerCase()

      switch (key.toLowerCase()) {
        case 'variationnumber':
          return normalizeText(item.variationNumber).includes(normalizedValue)
        case 'description':
          return normalizeText(item.description).includes(normalizedValue)
        case 'variationtype':
          return normalizeText(item.variationType).includes(normalizedValue)
        case 'reason':
          return normalizeText(item.reason).includes(normalizedValue)
        case 'fundingsource':
          return normalizeText(item.fundingSource).includes(normalizedValue)
        case 'contractname':
          return normalizeText(item.contractName).includes(normalizedValue)
        case 'workflowstatus':
        case 'statuslabel':
          return (
            normalizeText(item.workflowStatus).includes(normalizedValue) ||
            normalizeText(item.documentStatus).includes(normalizedValue) ||
            normalizeText(item.statusLabel).includes(normalizedValue)
          )
        case 'assessmentamount': {
          const amount = Number(value)
          if (Number.isNaN(amount)) return false
          return item.assessmentAmount === amount
        }
        case 'variationagedays': {
          const ageSearch = extractNumericSearchTerm(value)
          if (!ageSearch) return false
          if (item.variationAgeDays === null || item.variationAgeDays === undefined) return false
          return String(item.variationAgeDays).includes(ageSearch)
        }
        case 'datesubmitted': {
          const filterDate = normalizeDateFilterValue(rawValue)
          if (!filterDate) return false
          return item.dateSubmitted === filterDate
        }
        case 'responsedate': {
          const filterDate = normalizeDateFilterValue(rawValue)
          if (!filterDate) return false
          return item.responseDate === filterDate
        }
        default:
          return true
      }
    })
  )
}

export const listVariationAssessments = async (options?: {
  projectId?: string | number
  status?: string
  search?: string
  skip?: number
  take?: number
  sort?: string
  filter?: string
}): Promise<PaginatedResult<VariationAssessmentSummary>> => {
  if (API_URL) {
    try {
      const params: Record<string, string> = {}
      if (options?.projectId) params.projectId = options.projectId.toString()
      if (options?.status) params.status = options.status
      if (options?.search) params.search = options.search
      if (options?.skip !== undefined) params.skip = options.skip.toString()
      if (options?.take !== undefined) params.take = options.take.toString()
      if (options?.sort) params.sort = options.sort
      if (options?.filter) params.filter = options.filter

      const { data } = await axios.get<PaginatedResult<VariationAssessmentSummary> | any>(`${API_URL}`, { params })
      return {
        items: data.items ?? data.Items ?? [],
        totalCount: data.totalCount ?? data.TotalCount ?? 0,
        pageNumber: data.pageNumber ?? data.PageNumber ?? 1,
        totalPages: data.totalPages ?? data.TotalPages ?? 1,
      }
    } catch (error) {
      console.warn('Falling back to mock variation summaries.', error)
    }
  }

  await delay(400)
  let filteredItems = [...mockVariationSummaries]

  if (options?.status) {
    const normalizedStatus = options.status.trim().toLowerCase()
    filteredItems = filteredItems.filter(
      (item) =>
        item.workflowStatus?.toLowerCase() === normalizedStatus ||
        item.documentStatus?.toLowerCase() === normalizedStatus
    )
  }

  filteredItems = applyMockSearch(filteredItems, options?.search)
  filteredItems = applyMockFilters(filteredItems, options?.filter)

  const sortedItems = sortMockVariationSummaries(filteredItems, options?.sort)
  const skip = options?.skip ?? 0
  const take = options?.take && options.take > 0 ? options.take : sortedItems.length
  const pagedItems = sortedItems.slice(skip, skip + take)
  const totalCount = sortedItems.length
  const totalPages = take > 0 ? Math.max(1, Math.ceil(totalCount / take)) : 1
  const pageNumber = take > 0 ? Math.floor(skip / take) + 1 : 1
  return {
    items: pagedItems,
    totalCount,
    pageNumber,
    totalPages,
  }
}

export const getVariationAssessment = async (options?: { variationId?: string; projectId?: number | string | null }): Promise<VariationAssessment> => {
  const variationId = options?.variationId
  if (API_URL) {
    try {
      const url = variationId ? `${API_URL}/${variationId}` : `${API_URL}/template`
      const params: Record<string, string> = {}
      if (!variationId && options?.projectId) {
        params.projectId = options.projectId.toString()
      }
      const { data } = await axios.get<VariationAssessment>(url, { params })
      return data
    } catch (error) {
      console.warn('Falling back to mock variation assessment.', error)
    }
  }

  await delay()
  return mockVariationAssessment
}

export const saveVariationAssessmentDraft = async (payload: VariationAssessment): Promise<VariationAssessment> => {
  if (API_URL) {
    const { data } = await axios.post<VariationAssessment>(API_URL, payload)
    return data
  }

  await delay(500)
  return payload
}

export const createVariationAssessmentRevision = async (variationId: string): Promise<VariationAssessment> => {
  if (API_URL) {
    try {
      const { data } = await axios.post<VariationAssessment>(`${API_URL}/${variationId}/revision`)
      return data
    } catch (error) {
      console.warn('Create variation revision fallback to mock implementation.', error)
    }
  }

  await delay(300)
  return {
    ...mockVariationAssessment,
    meta: {
      ...mockVariationAssessment.meta,
      id: `${Date.now()}`,
      mode: 'edit',
      revision: (mockVariationAssessment.meta.revision ?? 0) + 1,
      status: 'draft',
      statusLabel: 'Draft',
      lastUpdated: new Date().toISOString(),
    },
    details: {
      ...mockVariationAssessment.details,
      variationNumber: appendNextRevisionSuffix(mockVariationAssessment.details.variationNumber),
      documentStatus: 'draft',
    },
  }
}

export const unlockVariationAssessment = async (variationId: string): Promise<VariationAssessment> => {
  const trimmedId = variationId?.trim()
  if (!trimmedId) {
    throw new Error('Variation ID is required for unlock.')
  }

  if (!API_URL) {
    throw new Error('Unlock action requires an API connection.')
  }

  const { data } = await axios.post<VariationAssessment>(`${API_URL}/${trimmedId}/unlock`)
  return data
}

export const submitVariationAssessment = async (payload: VariationAssessment): Promise<VariationAssessment> => {
  if (API_URL) {
    try {
      const { data } = await axios.post<VariationAssessment>(`${API_URL}/submit`, payload)
      return data
    } catch (error) {
      console.warn('Submit variation fallback to mock implementation.', error)
    }
  }

  await delay(800)
  return payload
}

export const deleteVariationAssessment = async (variationId: string): Promise<void> => {
  const trimmedId = variationId?.trim()
  if (!trimmedId) {
    throw new Error('Variation ID is required for deletion.')
  }

  if (!API_URL) {
    throw new Error('Delete action requires an API connection.')
  }

  await axios.delete(`${API_URL}/${trimmedId}`)
}

export const generateVariationAssessmentReport = async (variationId: string) => {
  if (API_URL) {
    try {
      const { data } = await axios.post(`${API_URL}/${variationId}/report`, undefined, { responseType: 'blob' })
      return data as Blob
    } catch (error) {
      console.warn('Generate report fallback to mock implementation.', error)
    }
  }

  await delay(800)
  return new Blob(['Mock variation report'], { type: 'application/pdf' })
}

export const searchVariationAssessmentUsers = async (searchTerm: string, roles: string[]) => {
  if (!API_URL) {
    return [] as VariationUserLookupItem[]
  }

  const params = new URLSearchParams()
  if (searchTerm) {
    params.append('term', searchTerm)
  }
  roles.forEach((role) => params.append('roles', role))

  const { data } = await axios.get<VariationUserLookupItem[]>(`${API_URL}/users`, { params })
  return data
}

export const getVariationAssessmentRoles = async (keyword?: string) => {
  if (!ROLE_API_URL) {
    return [] as RoleLookupItem[]
  }

  const params = new URLSearchParams()
  if (keyword) {
    params.append('keyword', keyword)
  }

  const { data } = await axios.get<RoleLookupItem[]>(`${ROLE_API_URL}/getallroles`, { params })
  return data
}

export const getVariationAssessmentUserSignature = async () => {
  if (!API_URL) {
    return undefined
  }

  try {
    const response = await axios.get<UserSignatureResponse>(`${API_URL}/user-signatures`)
    if (response.status === 204 || !response.data?.fileName) {
      return undefined
    }
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return undefined
    }
    throw error
  }
}

export const buildVariationUserSignatureUrl = (fileName?: string) => {
  if (!API_URL || !fileName) {
    return undefined
  }
  return `${API_URL}/user-signatures/current/${encodeURIComponent(fileName)}`
}

export type SignatureSlot = 'preparedBy' | 'approvedBy'

export type SignatureUploadResponse = {
  fileName: string
  url?: string
  contentType?: string
  size?: number
}

export const uploadVariationSignature = async (
  variationId: string,
  slot: SignatureSlot,
  file: File
): Promise<SignatureUploadResponse> => {
  if (!API_URL) {
    throw new Error('Signature upload requires an API URL. Configure REACT_APP_API_URL to enable uploads.')
  }

  const formData = new FormData()
  formData.append('file', file)

  const { data } = await axios.post<SignatureUploadResponse>(`${API_URL}/${variationId}/signatures/${slot}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return {
    fileName: data.fileName,
    url: `${API_URL}/${variationId}/signatures/${encodeURIComponent(data.fileName)}`,
    contentType: data.contentType,
    size: data.size,
  }
}

export const buildVariationSignatureUrl = (variationId?: string, fileName?: string) => {
  if (!API_URL || !variationId || !fileName) {
    return undefined
  }
  return `${API_URL}/${variationId}/signatures/${encodeURIComponent(fileName)}`
}
